// CompanyListPage.jsx
import React, { useState, useEffect } from 'react';
import axiosConfig from "../../api/axiosConfig";

const CompanyListPage = () => {
  const [companies, setCompanies] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [formData, setFormData] = useState({ sirket_adi: '' });

  // Arama ve filtreleme
  const [searchTerm, setSearchTerm] = useState(''); // sirket_adi
  const [filterType, setFilterType] = useState('all'); // "all", "updated", "not-updated"

  // Tarih aralığı filtre
  const [startDate, setStartDate] = useState(''); // created_at başlangıç
  const [endDate, setEndDate] = useState('');     // created_at bitiş

  // Toplu silme için seçili şirket ID'leri
  const [selectedIds, setSelectedIds] = useState([]);

  // Şirket listesini çek
  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axiosConfig.get('/admin/sirkets');
      setCompanies(response.data);
      setSelectedIds([]);
    } catch (error) {
      console.error('Şirketleri çekerken hata oluştu:', error);
    }
  };

  // Formu yeni şirket ekleme için aç
  const openFormForCreate = () => {
    setCurrentCompany(null);
    setFormData({ sirket_adi: '' });
    setIsFormOpen(true);
  };

  // Formu düzenleme için aç
  const openFormForEdit = (company) => {
    setCurrentCompany(company);
    setFormData({ sirket_adi: company.sirket_adi });
    setIsFormOpen(true);
  };

  // Formu kapat
  const closeForm = () => {
    setIsFormOpen(false);
    setCurrentCompany(null);
    setFormData({ sirket_adi: '' });
  };

  // Ekle / Güncelle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentCompany) {
        // Güncelle
        await axiosConfig.put(`/admin/sirkets/${currentCompany.id}`, {
          sirket_adi: formData.sirket_adi
        });
      } else {
        // Ekle
        await axiosConfig.post('/admin/sirkets', {
          sirket_adi: formData.sirket_adi
        });
      }
      fetchCompanies();
      closeForm();
    } catch (error) {
      console.error('handleSubmit error:', error);
    }
  };

  // Tekil sil
  const handleDelete = async (id) => {
    if (!window.confirm('Bu şirketi silmek istediğinizden emin misiniz?')) return;
    try {
      await axiosConfig.delete(`/admin/sirkets/${id}`);
      fetchCompanies();
    } catch (error) {
      console.error('handleDelete error:', error);
    }
  };

  // Toplu sil
  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm('Seçili şirketleri silmek istediğinizden emin misiniz?')) return;

    try {
      // Örnek: her ID için ayrı istek atıyoruz
      for (const id of selectedIds) {
        await axiosConfig.delete(`/admin/sirkets/${id}`);
      }
      fetchCompanies();
    } catch (error) {
      console.error('Toplu silme sırasında hata:', error);
    }
  };

  // Checkbox değişimi (tek satır)
  const handleSelectChange = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((selId) => selId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Tüm checkbox'ları seç / kaldır
  const handleSelectAll = () => {
    if (selectedIds.length === filteredCompanies.length && filteredCompanies.length > 0) {
      setSelectedIds([]);
    } else {
      const allIds = filteredCompanies.map((company) => company.id);
      setSelectedIds(allIds);
    }
  };

  // Arama metni
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filtre seçimi
  const handleFilterChange = (e) => {
    setFilterType(e.target.value);
  };

  // Tarih seçimi
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };
  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  // Filtrelenmiş liste
  const filteredCompanies = companies.filter((company) => {
    // 1) Arama
    const matchesSearch = company.sirket_adi
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    // 2) updated_at
    let matchesFilter = true;
    if (filterType === 'updated') {
      matchesFilter = !!company.updated_at;
    } else if (filterType === 'not-updated') {
      matchesFilter = !company.updated_at;
    }

    // 3) Tarih aralığı (created_at)
    let matchesDateRange = true;
    if (startDate) {
      const cDate = new Date(company.created_at);
      const sDate = new Date(startDate);
      if (cDate < sDate) {
        matchesDateRange = false;
      }
    }
    if (endDate) {
      const cDate = new Date(company.created_at);
      const eDate = new Date(endDate);
      if (cDate > eDate) {
        matchesDateRange = false;
      }
    }

    return matchesSearch && matchesFilter && matchesDateRange;
  });

  const allSelected = selectedIds.length === filteredCompanies.length && filteredCompanies.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-full bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Üst başlık */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <h2 className="text-3xl font-bold text-white flex items-center">
              <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Şirket Yönetimi
            </h2>
            <div className="flex gap-3">
              <button
                className="bg-red-500 text-white px-5 py-2.5 rounded-lg hover:bg-red-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg"
                onClick={handleBatchDelete}
                disabled={selectedIds.length === 0}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Seçili Sil ({selectedIds.length})
              </button>
              <button
                className="bg-emerald-500 text-white px-5 py-2.5 rounded-lg hover:bg-emerald-600 transition-all duration-200 flex items-center shadow-lg"
                onClick={openFormForCreate}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Yeni Şirket
              </button>
            </div>
          </div>
        </div>

        {/* Filtreler */}
        <div className="bg-gray-50 px-8 py-6 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Şirket Ara</label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                  placeholder="Şirket adı..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <svg className="w-5 h-5 absolute left-3 top-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Güncellenme Durumu</label>
              <select
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                value={filterType}
                onChange={handleFilterChange}
              >
                <option value="all">Tümü</option>
                <option value="updated">Güncellenmiş</option>
                <option value="not-updated">Güncellenmemiş</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Başlangıç Tarihi</label>
              <input
                type="date"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                value={startDate}
                onChange={handleStartDateChange}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Bitiş Tarihi</label>
              <input
                type="date"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                value={endDate}
                onChange={handleEndDateChange}
              />
            </div>
          </div>
        </div>

        {/* Tablo */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Şirket Adı</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Oluşturma Tarihi</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Güncellenme Tarihi</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredCompanies.map((company) => {
                const isSelected = selectedIds.includes(company.id);
                return (
                  <tr key={company.id} className={`${isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'} transition-colors`}>
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectChange(company.id)}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center mr-3">
                          <span className="text-white font-semibold">{company.sirket_adi.charAt(0).toUpperCase()}</span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{company.sirket_adi}</div>
                          <div className="text-sm text-gray-500">Kurumsal</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {company.created_at
                          ? new Date(company.created_at).toLocaleDateString('tr-TR')
                          : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {company.updated_at ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          {new Date(company.updated_at).toLocaleDateString('tr-TR')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Güncellenmemiş
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          className="text-purple-600 hover:text-purple-900 transition-colors"
                          onClick={() => openFormForEdit(company)}
                          title="Düzenle"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          className="text-red-600 hover:text-red-900 transition-colors"
                          onClick={() => handleDelete(company.id)}
                          title="Sil"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {filteredCompanies.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <p className="text-gray-500 text-lg font-medium">Kayıt bulunamadı</p>
                      <p className="text-gray-400 text-sm mt-1">Arama kriterlerinizi değiştirmeyi deneyin</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Tablo altı bilgi */}
        {filteredCompanies.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Toplam <span className="font-semibold">{filteredCompanies.length}</span> şirket gösteriliyor
            </p>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-5 rounded-t-2xl">
              <h3 className="text-xl font-bold text-white flex items-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {currentCompany ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  )}
                </svg>
                {currentCompany ? 'Şirketi Düzenle' : 'Yeni Şirket Ekle'}
              </h3>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-6">
                <label htmlFor="sirket_adi" className="block text-sm font-medium text-gray-700 mb-2">
                  Şirket Adı
                </label>
                <div className="relative">
                  <input
                    id="sirket_adi"
                    name="sirket_adi"
                    type="text"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                    placeholder="Şirket adını giriniz..."
                    value={formData.sirket_adi}
                    onChange={(e) => setFormData({ ...formData, sirket_adi: e.target.value })}
                    required
                  />
                  <svg className="w-5 h-5 absolute left-3 top-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  onClick={closeForm}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all font-medium shadow-lg"
                >
                  {currentCompany ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyListPage;